<template>
	<!-- <glass-card class="nutrition-container"> -->
	<div class="nutrition-container">
		<!-- <h5 class="text-center fw-bold text-white">
			<i class="bi bi-calendar-check"></i> Today's Nutrition
		</h5> -->
		<div class="nutrition">
			<div class="container-fluid">
				<div class="nutrition-grid">
					<div v-for="(value, name) in nutrients" :key="name">
						<glass-card class="nutrition-item">
							<div class="nutrition-header">
								<span class="title">{{ name }}</span>
								<i
									:class="getIconForNutrient(name)"
									:style="{ color: getIconColor(name) }"></i>
							</div>
							<div
								class="nutrition-value"
								:class="{ over: value.max > 0 && value.value > value.max }">
								<span class="number">{{ formatValue(value.value, name) }}</span>
								<span class="unit">{{ getUnit(name) }}</span>
							</div>
							<div
								class="progress mt-2 compact"
								:class="{ invisible: value.max <= 0 }">
								<div
									class="progress-bar"
									:class="getProgressBarClass(value)"
									:style="{ width: getProgressWidth(value) + '%' }"></div>
							</div>
							<div class="target" :class="{ invisible: value.max <= 0 }">
								Target {{ value.max }} {{ getUnit(name) }}
							</div>
						</glass-card>
					</div>
				</div>
			</div>
		</div>
	</div>
	<!-- </glass-card> -->
</template>

<script setup>
	import { ref, watch, onMounted } from "vue"
	// import { useTokenStore } from "@/stores/token"
	import { useRoute } from "vue-router"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	import {
		lastMealUpdated,
		lastMealDeleted,
		lastMealAdded,
	} from "@/utils/mealBus"
	import GlassCard from "./GlassCard.vue"

	const nutrients = ref({})
	const route = useRoute()
	// const tokenStore = useTokenStore()

	const getIconForNutrient = (name) => {
		const icons = {
			Calories: "bi bi-fire",
			Protein: "bi bi-egg-fried",
			Carbs: "bi bi-circle-square",
			Fats: "bi bi-droplet-fill",
			Sugars: "bi bi-cup-straw",
			Fiber: "bi bi-flower1",
			Sodium: "bi bi-water",
		}
		return icons[name] || "bi bi-info-circle"
	}

	const getProgressWidth = (value) => {
		if (value.max <= 0) return 0
		const percentage = (value.value / value.max) * 100
		return Math.min(percentage, 100)
	}

	const getProgressBarClass = (value) => {
		if (value.max <= 0) return "bg-secondary"
		const percentage = (value.value / value.max) * 100

		if (percentage > 100) return "bg-danger"
		if (percentage > 90) return "bg-danger bg-opacity-75"
		if (percentage > 80) return "bg-warning"
		if (percentage > 70) return "bg-warning bg-opacity-75"
		if (percentage > 60) return "bg-success bg-opacity-75"
		return "bg-success"
	}

	const getUnit = (name) => {
		if (name === "Calories") return "kcal"
		if (name === "Sodium") return "mg"
		return "g"
	}

	const formatValue = (rawValue, name) => {
		const numericValue = Number(rawValue)
		if (!Number.isFinite(numericValue)) return rawValue
		if (name === "Calories" || name === "Sodium")
			return Math.round(numericValue)
		return numericValue.toFixed(1)
	}

	const getIconColor = (name) => {
		const colors = {
			Calories: "#fb923c", // orange
			Protein: "#22c55e", // green
			Carbs: "#a3e635", // lime
			Fats: "#ef4444", // red
			Sugars: "#f59e0b", // amber
			Fiber: "#2dd4bf", // teal
			Sodium: "#60a5fa", // blue
		}
		return colors[name] || "#9aa3ab"
	}

	const fetchNutritionalSummary = async () => {
		try {
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth("/api/daily-nutritional-summary", {
				method: "GET",
				headers: {
					"User-Timezone": tz,
					// Authorization: `Bearer ${tokenStore.token}`,
				},
			})

			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			if (!response.ok) {
				throw new Error("Failed to fetch nutritional summary")
			}
			nutrients.value = await response.json()
			// }
		} catch (error) {
			console.error("Error:", error)
			// Handle errors, e.g., by showing a message to the user
		}
	}

	// watch(
	// 	() => route.fullPath,
	// 	(newPath, oldPath) => {
	// 		if (oldPath && newPath) {
	// 			fetchNutritionalSummary()
	// 		}
	// 	}
	// )

	onMounted(() => {
		fetchNutritionalSummary()
	})

	watch(lastMealUpdated, (v) => {
		fetchNutritionalSummary()
	})

	watch(lastMealDeleted, (v) => {
		fetchNutritionalSummary()
	})

	watch(
		lastMealAdded,
		(v) => {
			if (v.length > 0) fetchNutritionalSummary()
		},
		{ deep: true }
	)
</script>

<style lang="less" scoped>
	@import "../assets/colors.less";

	.nutrition-container {
		border-radius: 8px;
		overflow: hidden;
		> h5 {
			background-color: #007000;
			padding: 10px;
			margin: 0;
		}
	}

	.nutrition {
		// color: @color4;
		color: #ff6400;
		// background-color: #045704;
		// background-color: #f8f9fa;
		font-weight: bold;
		padding: 15px;
		// box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
	}

	.nutrition-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 10px 10px;
		// justify-items: center;
		justify-items: stretch;
	}

	/* Make the final lone card span the full width on small screens to avoid awkward empty space */
	@media (max-width: 430px) {
		.nutrition-grid > div:last-child {
			grid-column: 1 / -1;
		}
	}

	/* Small phones in landscape and small tablets: make columns narrower so two fit better around 400–480px */
	@media (min-width: 420px) and (max-width: 767px) {
		.nutrition-grid {
			grid-template-columns: repeat(2, minmax(180px, 1fr));
			gap: 12px 12px;
		}
	}

	/* Tablet */
	@media (min-width: 768px) {
		.nutrition-grid {
			grid-template-columns: repeat(2, minmax(260px, 1fr));
			gap: 14px 14px;
		}
	}

	/* Large screens */
	@media (min-width: 992px) {
		.nutrition-grid {
			grid-template-columns: repeat(3, minmax(260px, 1fr));
			gap: 16px 16px;
		}
	}

	/* Extra-large screens */
	@media (min-width: 1280px) {
		.nutrition-grid {
			grid-template-columns: repeat(4, minmax(260px, 1fr));
		}
	}

	.nutrition-item {
		margin: 0; /* handled by grid gap */
		padding: 12px 14px;
		width: 100%;
		min-height: 110px; // compact on mobile
		text-align: left;
		transition: transform 0.2s ease;
		position: relative;
	}

	@media (min-width: 768px) {
		.nutrition-item {
			padding: 14px 16px;
			min-height: 120px;
		}
	}

	.nutrition-item:hover {
		transform: translateY(-2px);
	}

	.nutrition-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 6px;
		color: #d1d5db;
		.text {
			opacity: 0.85;
		}
		i {
			font-size: 1.1rem;
		}
	}

	.nutrition-value {
		display: flex;
		align-items: baseline;
		gap: 8px;
		font-weight: 700;
		// color: #111827;
		// color: white;
		color: #85bca1;
		.number {
			font-size: 1.6rem;
			line-height: 1;
			// color: #111827;
			color: white;
		}
		.unit {
			font-size: 0.9rem;
			// color: #6b7280;
			color: #85bca1;
			font-weight: 600;
		}
	}

	.over {
		color: #ff6b6b;
		animation: pulse 1.5s infinite;
	}

	@keyframes pulse {
		0% {
			opacity: 1;
		}
		50% {
			opacity: 0.7;
		}
		100% {
			opacity: 1;
		}
	}

	small {
		font-size: 12px;
		opacity: 0.8;
	}

	.progress.compact {
		height: 6px;
		background-color: rgba(17, 24, 39, 0.25);
		border-radius: 9999px;
		overflow: hidden;
	}

	.invisible {
		visibility: hidden;
	}

	.target {
		margin-top: 6px;
		font-size: 12px;
		color: #9ca3af;
		font-weight: 600;
	}

	h6 {
		i {
			margin-right: 5px;
		}
	}
</style>
